/**
 * Team Voice Split Node - Player View
 *
 * Tactical team deployment view showing players being moved
 * to their team-specific voice channels.
 * Uses auto_balance-style team card layout with per-player
 * move status indicators and per-team progress.
 * Supports 2-8 teams, 2-100 players, parties.
 */

import { AnimatePresence, motion } from "framer-motion";
import { Check, Info } from "lucide-react";
import { NodeCard } from "../../components/NodeCard";
import { NodeHeader } from "../../components/NodeHeader";
import type { NodePlayerViewProps } from "../types";

type TeamVoiceSplitState = {
  movedPlayers: string[];
  failedPlayers: string[];
  noDiscord?: boolean;
};

export function TeamVoiceSplitView({
  nodeState,
  players,
}: NodePlayerViewProps<TeamVoiceSplitState>) {
  const state = nodeState.state as TeamVoiceSplitState;
  const config = nodeState.config as { labels?: { title?: string; subtitle?: string } };
  const title = config.labels?.title ?? "Team Voice Split";
  const subtitle = config.labels?.subtitle ?? "Moving players to team channels";

  const movedCount = state.movedPlayers.length;
  const totalCount = players.length;
  const progress = totalCount > 0 ? movedCount / totalCount : 0;
  const allMoved = movedCount === totalCount;

  return (
    <NodeCard>
      <NodeHeader title={title} subtitle={subtitle} />

      {/* noDiscord — voice unavailable, skip the full UI */}
      {state.noDiscord ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="flex justify-center"
        >
          <div className="inline-flex items-center gap-2.5 px-5 py-3 rounded-lg bg-surface-800/80 border border-surface-700/50">
            <Info size={16} className="text-surface-500 shrink-0" aria-hidden="true" />
            <span className="text-sm text-text-secondary">
              Voice chat is not configured for this server
            </span>
          </div>
        </motion.div>
      ) : (
        <>
          {/* Overall progress bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex flex-col items-stretch gap-1 mx-auto max-w-xs"
          >
            <div className="flex items-center justify-between px-0.5">
              <span className="text-xs text-text-secondary">
                <span className="font-display font-bold text-text-primary tabular-nums">
                  {movedCount}
                </span>
                {" / "}
                <span className="tabular-nums">{totalCount}</span>
                {" players moved"}
              </span>
              <span className="text-xs font-medium text-text-secondary tabular-nums">
                {Math.round(progress * 100)}%
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-surface-800 overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-brand-500 to-brand-400"
                initial={{ width: 0 }}
                animate={{ width: `${progress * 100}%` }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              />
            </div>
          </motion.div>

          {/* All moved indicator */}
          <AnimatePresence>
            {allMoved && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="mt-4 text-center"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500/[0.07] border border-brand-500/20">
                  <Check size={14} className="text-brand-400" aria-hidden="true" />
                  <span className="text-sm text-text-secondary">
                    All players in{" "}
                    <span className="font-display font-semibold text-brand-400">team channels</span>
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </NodeCard>
  );
}
